<template>
  <div>
    <h1 class="font-bold text-2xl text-primary-500">
      {{ isEdit ? 'Editar Plantilla' : 'Crear Plantilla' }}
    </h1>

    <div class="mt-8">
      <!-- Step 1: Basic Information -->
      <div v-if="currentStep === 1" class="card">
        <h2 class="font-semibold text-lg mb-4">Información Básica</h2>
        
        <div class="space-y-4">
          <div>
            <label class="label">Nombre de la plantilla *</label>
            <input
              v-model="template.name"
              type="text"
              class="input"
              placeholder="Ej: Dieta de Transición Estándar"
            />
          </div>

          <div>
            <label class="label">Descripción</label>
            <textarea
              v-model="template.description"
              class="input"
              rows="3"
              placeholder="Descripción opcional de la plantilla"
            ></textarea>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="label">Tipo de plantilla *</label>
              <custom-select
                v-model="template.template_type"
                :options="getTemplateTypeOptions()"
              />
            </div>

            <div>
              <label class="label">Duración (días)</label>
              <input
                v-model.number="template.duration_days"
                type="number"
                class="input"
                placeholder="Opcional"
                min="1"
              />
            </div>
          </div>

          <div>
            <label class="flex items-center">
              <input
                v-model="template.is_active"
                type="checkbox"
                class="mr-2"
              />
              <span>Plantilla activa</span>
            </label>
          </div>
        </div>

        <div class="mt-6 flex justify-end">
          <button
            type="button"
            class="primary-button"
            @click="currentStep = 2"
          >
            Siguiente: Crear Comidas
          </button>
        </div>
      </div>

      <!-- Step 2: Meals and Portions -->
      <div v-if="currentStep === 2">
        <div class="card mb-4">
          <div class="flex justify-between items-center">
            <h2 class="font-semibold text-lg">Comidas y Porciones</h2>
            <div class="text-sm">
              <p class="font-medium">
                Comida {{ selectedMeal }}: {{ getMealTotal(selectedMeal - 1) }}%
              </p>
              <p
                class="mt-1"
                :class="{
                  'text-green-600': Math.abs(getMealTotal(selectedMeal - 1) - 100) < 0.01,
                  'text-red-600': Math.abs(getMealTotal(selectedMeal - 1) - 100) >= 0.01
                }"
              >
                {{ getMealPercentageStatus(selectedMeal - 1) }}
              </p>
            </div>
          </div>
        </div>

        <!-- Meal Tabs -->
        <div class="flex gap-2 mb-4 overflow-x-auto">
          <button
            v-for="mealNum in 7"
            :key="mealNum"
            type="button"
            class="px-4 py-2 rounded flex items-center gap-2 whitespace-nowrap"
            :class="[
              selectedMeal === mealNum
                ? 'bg-primary-500 text-white'
                : 'bg-gray-200 hover:bg-gray-300'
            ]"
            @click="selectedMeal = mealNum"
          >
            Comida {{ mealNum }}
            <span
              class="w-2 h-2 rounded-full"
              :class="getMealStatusColor(mealNum)"
            ></span>
          </button>
        </div>

        <!-- Selected Meal Content -->
        <div class="card">
          <h3 class="font-medium text-primary-500 mb-4">
            Comida {{ selectedMeal }}
          </h3>

          <div class="mb-4">
            <label class="label">Comentarios (opcional)</label>
            <textarea
              v-model="meals[selectedMeal - 1].comments"
              class="input"
              rows="2"
              placeholder="Comentarios para esta comida"
            ></textarea>
          </div>

          <!-- Copy to next meal button -->
          <button
            v-if="selectedMeal < 7 && meals[selectedMeal - 1].portions.length > 0 && meals[selectedMeal - 1].portions.some(p => p.name)"
            type="button"
            class="secondary-button mb-4"
            @click="copyToNextMeal(selectedMeal - 1)"
          >
            📋 Copiar a Comida {{ selectedMeal + 1 }}
          </button>

          <!-- Portions for this meal -->
          <div class="space-y-4">
            <div
              v-for="(portion, index) in meals[selectedMeal - 1].portions"
              :key="index"
              class="p-4 bg-gray-50 rounded"
            >
              <div class="flex justify-between items-start mb-3">
                <h4 class="font-medium">Porción {{ index + 1 }}</h4>
                <button
                  v-if="meals[selectedMeal - 1].portions.length > 1"
                  type="button"
                  class="text-error-500 hover:text-error-700"
                  @click="removePortion(selectedMeal - 1, index)"
                >
                  Eliminar
                </button>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label class="label">Nombre *</label>
                  <input
                    v-model="portion.name"
                    type="text"
                    class="input"
                    placeholder="Ej: Proteína principal"
                  />
                </div>

                <div>
                  <label class="label">Porcentaje (%) *</label>
                  <input
                    v-model.number="portion.percentage"
                    type="number"
                    class="input"
                    placeholder="Ej: 25"
                    min="0"
                    max="100"
                    step="0.01"
                  />
                </div>

                <div>
                  <label class="label">Categoría *</label>
                  <custom-select
                    v-model="portion.category"
                    :options="getCategoryOptions()"
                    @change="onCategoryChange(portion, $event)"
                  />
                </div>

                <div>
                  <label class="label">Subcategoría *</label>
                  <custom-select
                    v-model="portion.subcategory"
                    :options="getSubcategoryOptions(portion.category)"
                    :disabled="!portion.category"
                    @change="onSubcategoryChange(portion, $event)"
                  />
                </div>
              </div>
            </div>

            <button
              type="button"
              class="secondary-button"
              @click="addPortion(selectedMeal - 1)"
            >
              + Agregar Porción
            </button>
          </div>

          <div class="mt-4 p-3 bg-blue-50 rounded">
            <p class="text-sm font-medium text-blue-800">
              Total de esta comida: {{ getMealTotal(selectedMeal - 1) }}%
            </p>
          </div>
        </div>

        <div class="mt-6 flex justify-between">
          <button
            type="button"
            class="secondary-button"
            @click="currentStep = 1"
          >
            Anterior
          </button>
          <button
            type="button"
            class="primary-button"
            :disabled="!canSave"
            @click="saveTemplate"
          >
            {{ isEdit ? 'Actualizar Plantilla' : 'Crear Plantilla' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import api from "@/api";
import CustomSelect from "@/components/ui/CustomSelect";

export default {
  name: "DietTemplatesForm",
  
  components: {
    CustomSelect
  },

  data() {
    return {
      currentStep: 1,
      selectedMeal: 1,
      template: {
        name: "",
        description: "",
        template_type: "",
        duration_days: null,
        is_active: true
      },
      meals: [],
      categories: [],
      loading: false
    };
  },

  computed: {
    ...mapState({
      user: "user"
    }),

    isEdit() {
      return !!this.$route.params.id;
    },

    canProceedToStep2() {
      return (
        this.template.name &&
        this.template.template_type
      );
    },

    canSave() {
      return this.allMealsValid() && this.allMealsHaveCorrectPercentages();
    }
  },

  mounted() {
    this.$store.commit("setSelectedTab", "dietTemplates");
    this.initializeMeals();
    this.loadCategories();
    
    console.log("Form mounted - isEdit:", this.isEdit, "id:", this.$route.params.id);
    
    if (this.isEdit) {
      this.loadTemplate();
    }
  },

  methods: {
    initializeMeals() {
      this.meals = Array.from({ length: 7 }, (_, i) => ({
        meal_number: i + 1,
        comments: "",
        portions: [
          {
            name: "",
            percentage: 0,
            category: "",
            subcategory: ""
          }
        ]
      }));
    },

    async loadCategories() {
      try {
        this.categories = await api.resources.getDietCategories();
      } catch (error) {
        this.$store.commit("setSnack", {
          message: "Error al cargar las categorías",
          color: "error"
        });
      }
    },

    async loadTemplate() {
      this.loading = true;
      try {
        const data = await api.dietTemplates.getById(this.$route.params.id);
        
        this.template = {
          name: data.name,
          description: data.description,
          template_type: data.template_type,
          duration_days: data.duration_days,
          is_active: data.is_active
        };
        
        // Load meals and portions
        this.meals = data.meals.map(meal => ({
          meal_number: meal.meal_number,
          comments: meal.comments || "",
          portions: meal.portions.map(portion => ({
            name: portion.name,
            percentage: parseFloat(portion.percentage),
            category: portion.category,
            subcategory: portion.subcategory,
            diet_category_id: portion.diet_category_id || null,
            diet_subcategory_id: portion.diet_subcategory_id || null
          }))
        }));
      } catch (error) {
        console.error("Error loading template:", error);
        this.$store.commit("setSnack", {
          message: "Error al cargar la plantilla",
          color: "error"
        });
        this.$router.push({ name: "dietTemplatesIndex" });
      } finally {
        this.loading = false;
      }
    },

    getSubcategories(categoryName) {
      const category = this.categories.find(c => c.name === categoryName);
      return category?.diet_subcategories || [];
    },
    
    getTemplateTypeOptions() {
      return [
        { value: '', label: 'Seleccionar tipo', disabled: false },
        { value: 'transition', label: 'Transición' },
        { value: 'standard', label: 'Estándar' },
        { value: 'special', label: 'Especial' }
      ];
    },
    
    getCategoryOptions() {
      const options = [{ value: '', label: 'Seleccionar categoría', disabled: false }];
      this.categories.forEach(category => {
        options.push({
          value: category.name,
          label: category.name
        });
      });
      return options;
    },
    
    getSubcategoryOptions(categoryName) {
      const options = [{ value: '', label: 'Seleccionar subcategoría', disabled: false }];
      const subcategories = this.getSubcategories(categoryName);
      subcategories.forEach(sub => {
        options.push({
          value: sub.name,
          label: sub.name
        });
      });
      return options;
    },

    addPortion(mealIndex) {
      this.meals[mealIndex].portions.push({
        name: "",
        percentage: 0,
        category: "",
        subcategory: "",
        diet_category_id: null,
        diet_subcategory_id: null
      });
    },

    removePortion(mealIndex, portionIndex) {
      this.meals[mealIndex].portions.splice(portionIndex, 1);
    },
    
    onCategoryChange(portion, event) {
      // Clear subcategory when category changes
      portion.subcategory = '';
      portion.diet_subcategory_id = null;
      
      // Find and set the category ID
      const category = this.categories.find(c => c.name === portion.category);
      if (category) {
        portion.diet_category_id = category.id;
      } else {
        portion.diet_category_id = null;
      }
    },
    
    onSubcategoryChange(portion, event) {
      // Find the category first
      const category = this.categories.find(c => c.name === portion.category);
      if (category) {
        // Find the subcategory within that category
        const subcategory = category.diet_subcategories.find(s => s.name === portion.subcategory);
        if (subcategory) {
          portion.diet_subcategory_id = subcategory.id;
        } else {
          portion.diet_subcategory_id = null;
        }
      }
    },

    copyToNextMeal(currentIndex) {
      if (currentIndex < 6 && this.meals[currentIndex]) {
        const currentMeal = this.meals[currentIndex];
        const copiedPortions = JSON.parse(JSON.stringify(currentMeal.portions));
        this.meals[currentIndex + 1].portions = copiedPortions;
        // Also copy comments if present
        if (currentMeal.comments) {
          this.meals[currentIndex + 1].comments = currentMeal.comments;
        }
        // Switch to the next meal tab
        this.selectedMeal = currentIndex + 2;
        this.$store.commit("setSnack", {
          message: `Porciones copiadas a Comida ${currentIndex + 2}`,
          color: "success"
        });
      }
    },

    getMealTotal(mealIndex) {
      return this.meals[mealIndex].portions
        .reduce((sum, portion) => sum + (portion.percentage || 0), 0)
        .toFixed(2);
    },

    getMealPercentageStatus(mealIndex) {
      const total = parseFloat(this.getMealTotal(mealIndex));
      if (Math.abs(total - 100) < 0.01) {
        return "✓ 100%";
      } else if (total > 100) {
        return `Exceso: ${(total - 100).toFixed(2)}%`;
      } else {
        return `Falta: ${(100 - total).toFixed(2)}%`;
      }
    },


    getMealStatusColor(mealNum) {
      const meal = this.meals[mealNum - 1];
      const total = meal.portions.reduce((sum, p) => sum + (p.percentage || 0), 0);
      const hasPortions = total > 0;
      const isComplete = meal.portions.every(p => 
        p.name && p.percentage > 0 && p.category && p.subcategory
      ) && Math.abs(total - 100) < 0.01;
      
      if (isComplete) {
        return "bg-green-500";
      } else if (total > 100) {
        return "bg-red-500";
      } else if (hasPortions) {
        return "bg-yellow-500";
      } else {
        return "bg-gray-400";
      }
    },

    allMealsValid() {
      return this.meals.every(meal => 
        meal.portions.length > 0 &&
        meal.portions.every(portion => 
          portion.name &&
          portion.percentage > 0 &&
          portion.category &&
          portion.subcategory
        )
      );
    },

    allMealsHaveCorrectPercentages() {
      return this.meals.every(meal => {
        const total = meal.portions.reduce((sum, p) => sum + (p.percentage || 0), 0);
        return Math.abs(total - 100) < 0.01;
      });
    },

    async saveTemplate() {
      if (!this.canSave) return;

      this.loading = true;
      try {
        // Convert Vue proxy to plain object
        const data = {
          ...this.template,
          meals: JSON.parse(JSON.stringify(this.meals))
        };
        
        console.log("Sending template data:", data);

        if (this.isEdit) {
          await api.dietTemplates.update(this.$route.params.id, data);
          this.$store.commit("setSnack", {
            message: "Plantilla actualizada exitosamente",
            color: "success"
          });
        } else {
          await api.dietTemplates.create(data);
          this.$store.commit("setSnack", {
            message: "Plantilla creada exitosamente",
            color: "success"
          });
        }

        this.$router.push({ name: "dietTemplatesIndex" });
      } catch (error) {
        console.error("Full error:", error);
        console.error("Error response:", error.response);
        this.$store.commit("setSnack", {
          message: error.response?.data?.message || error.message || "Error al guardar la plantilla",
          color: "error"
        });
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>